import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  getDownloads,
  refreshDownloads,
  type DailyDownloads,
  type DownloadSummary,
  type RemoteDailyMetric
} from '../lib/adminClient'
import type { ExploreRef } from '../lib/adminClient'
import { useAuth } from '../lib/auth'
import { useTableControls, type Column } from '../lib/useTableControls'
import { TableHead, TableToolbar } from '../components/TableControls'
import { DetailGrid, ExpandableRow, type Fact } from '../components/RowDetail'
import { Drillable, DrillValue } from '../components/Drill'

// Downloads — how many people took Plexii, and what happened next.
//
// The count comes from Cloudflare, which serves dl.plexiidesk.com, not from
// GA. GA's `download_click` is a click on a page we tag, so it cannot see a
// consent-decliner, a click in the download-link email the mobile form sends,
// an old or mirrored link, or the app updating itself. Both numbers are worth
// having; only this one is "downloads".
//
// Four things this page must never let you misread, each stated on the page
// itself rather than left to whoever remembers:
//   - our own machines are filtered out, and the filtered amount is shown;
//   - update checks and update payloads are NOT downloads, and are not shown
//     (owner, 2026-10-29: only new downloads and sign-ups);
//   - Cloudflare samples, so the figures are granular to the sample interval;
//   - a day nobody fetched from Cloudflare is unknown, not zero. It is a gap in
//     the charts and "Not fetched" in the table (owner, 2026-10-30: "real data,
//     not imagined").
//
// Every chart drills down: click a day (or focus a chart and use the arrow
// keys) and that day opens in the table underneath, which lists every figure
// the server stored for each day and downloads as CSV.
//
// Colours: violet = macOS, teal = Windows, amber = signups, checked with the
// dataviz palette validator against this console's dark surface (#0d1424) —
// worst all-pairs CVD ΔE 12.5, normal-vision 24.3, all three in the dark
// lightness band and above 3:1 contrast. The platform colours stay the same in
// every chart, because colour follows the entity.
const MAC = '#8b5cf6'
const WIN = '#0d9488'
const SIGNUP = '#d97706'

const RANGES = [7, 30, 90] as const

// A null value is a day nobody measured: drawn as a gap over a shaded band,
// never as a zero on the line.
type Series = { key: string; label: string; color: string; values: Array<number | null> }

function fmt(n: number): string {
  return n.toLocaleString('en-AU')
}

const fmtOr = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : fmt(n))

function short(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

const utc = (iso: string): string => `${iso.slice(0, 16).replace('T', ' ')} UTC`

// One chart. Never two y-scales: a measure on a different scale gets its own
// chart, which is why update checks (thousands) were never drawn over
// downloads (tens) however tempting the single-screen version is.
function TimeSeries({
  dates,
  series,
  selected,
  onSelect,
  height = 170
}: {
  dates: string[]
  series: Series[]
  selected: string | null
  onSelect: (date: string | null) => void
  height?: number
}): JSX.Element {
  const [hover, setHover] = useState<number | null>(null)
  const wrap = useRef<HTMLDivElement>(null)
  const W = 720
  const H = height
  const PAD_L = 34
  const PAD_B = 18
  const PAD_T = 8

  // Gridlines on round numbers, and the axis scaled to the TOP gridline rather
  // than to the data's max. Scaling to the raw max put the highest tick above
  // the plot — y(21) with max 20 is a negative coordinate — and its label was
  // clipped off the top of every chart whose max was not a multiple of the step.
  const peak = Math.max(1, ...series.flatMap((s) => s.values.filter((v): v is number => v !== null)))
  const { top, ticks } = useMemo(() => {
    const step = Math.max(1, Math.ceil(peak / 3))
    const t = step * Math.ceil(peak / step)
    const out: number[] = []
    for (let v = 0; v <= t; v += step) out.push(v)
    return { top: t, ticks: out }
  }, [peak])

  const n = dates.length
  const plotW = W - PAD_L - 8
  const x = (i: number): number => (n <= 1 ? PAD_L : PAD_L + (i * plotW) / (n - 1))
  const y = (v: number): number => PAD_T + (1 - v / top) * (H - PAD_T - PAD_B)
  const slot = n > 1 ? plotW / (n - 1) : plotW
  const unknown = dates.map((_, i) => series.every((s) => s.values[i] === null))
  const sel = selected ? dates.indexOf(selected) : -1

  function indexAt(clientX: number): number | null {
    const el = wrap.current
    if (!el || n === 0) return null
    const r = el.getBoundingClientRect()
    const px = ((clientX - r.left) / r.width) * W
    const i = Math.round(((px - PAD_L) / Math.max(1, plotW)) * (n - 1))
    return Math.min(n - 1, Math.max(0, i))
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (n === 0) return
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      const from = sel >= 0 ? sel : e.key === 'ArrowLeft' ? n : -1
      onSelect(dates[Math.min(n - 1, Math.max(0, from + (e.key === 'ArrowLeft' ? -1 : 1)))])
    } else if (e.key === 'Escape') onSelect(null)
  }

  // Runs of measured days; each is its own line and area, so a line never
  // bridges a day nobody measured.
  const runs = (values: Array<number | null>): Array<Array<[number, number]>> => {
    const out: Array<Array<[number, number]>> = []
    let cur: Array<[number, number]> = []
    values.forEach((v, i) => {
      if (v === null) {
        if (cur.length) out.push(cur)
        cur = []
      } else cur.push([i, v])
    })
    if (cur.length) out.push(cur)
    return out
  }

  const marker = (i: number, chosen: boolean): JSX.Element => (
    <g>
      <line
        x1={x(i)}
        x2={x(i)}
        y1={PAD_T}
        y2={H - PAD_B}
        stroke={chosen ? 'rgba(167,139,250,0.75)' : 'rgba(255,255,255,0.25)'}
        strokeWidth="1"
        strokeDasharray={chosen ? '3 3' : undefined}
      />
      {series.map((s) => {
        const v = s.values[i]
        // A 2px surface ring, so a marker sitting on another series stays
        // legible where the two lines cross.
        return v === null ? null : <circle key={s.key} cx={x(i)} cy={y(v)} r="4" fill={s.color} stroke="#0d1424" strokeWidth="2" />
      })}
    </g>
  )

  return (
    <div
      ref={wrap}
      className="relative cursor-pointer rounded focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/60"
      onMouseMove={(e) => setHover(indexAt(e.clientX))}
      onMouseLeave={() => setHover(null)}
      onClick={(e) => {
        const i = indexAt(e.clientX)
        if (i !== null) onSelect(dates[i] === selected ? null : dates[i])
      }}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="group"
      aria-label="Chart. Click a day, or use the arrow keys, to open it in the table below."
      data-testid="chart"
    >
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" className="block">
        {unknown.map((u, i) => {
          if (!u) return null
          const left = Math.max(PAD_L, x(i) - slot / 2)
          const right = Math.min(W - 8, x(i) + slot / 2)
          return <rect key={`u${i}`} x={left} width={Math.max(1, right - left)} y={PAD_T} height={H - PAD_T - PAD_B} fill="rgba(148,163,184,0.08)" data-unfetched={dates[i]} />
        })}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD_L} x2={W - 8} y1={y(t)} y2={y(t)} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            <text x={PAD_L - 6} y={y(t) + 3} textAnchor="end" fontSize="9" fill="#64748b">
              {t}
            </text>
          </g>
        ))}

        {series.map((s) => (
          <g key={s.key}>
            {runs(s.values).map((run, k) => {
              if (run.length === 1) {
                const [i, v] = run[0]
                return <circle key={k} cx={x(i)} cy={y(v)} r="2.5" fill={s.color} />
              }
              const path = run.map(([i, v], j) => `${j === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
              return (
                <g key={k}>
                  <path d={`${path} L${x(run[run.length - 1][0])},${H - PAD_B} L${x(run[0][0])},${H - PAD_B} Z`} fill={s.color} fillOpacity="0.1" />
                  <path d={path} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" />
                </g>
              )
            })}
          </g>
        ))}

        {sel >= 0 && marker(sel, true)}
        {hover !== null && hover !== sel && marker(hover, false)}

        <text x={PAD_L} y={H - 4} fontSize="9" fill="#64748b">
          {dates[0] ? short(dates[0]) : ''}
        </text>
        <text x={W - 8} y={H - 4} fontSize="9" fill="#64748b" textAnchor="end">
          {dates[n - 1] ? short(dates[n - 1]) : ''}
        </text>
      </svg>

      {hover !== null && dates[hover] && (
        <div
          className="pointer-events-none absolute top-0 rounded-lg px-2.5 py-1.5 text-[11px]"
          style={{
            left: `${Math.min(78, Math.max(0, ((x(hover) - PAD_L) / W) * 100))}%`,
            background: 'rgba(13,20,36,0.96)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
          data-testid="chart-tooltip"
        >
          <div className="text-slate-400 mb-0.5">{short(dates[hover])}</div>
          {unknown[hover] ? (
            <div className="text-slate-300">Not fetched from Cloudflare: no figure</div>
          ) : (
            series.map((s) => (
              <div key={s.key} className="flex items-center gap-1.5 text-slate-200">
                <span className="inline-block rounded-sm" style={{ width: 8, height: 8, background: s.color }} />
                {s.label} <span className="font-semibold">{fmtOr(s.values[hover])}</span>
              </div>
            ))
          )}
          <div className="text-slate-500 mt-0.5">Click to open this day below</div>
        </div>
      )}
    </div>
  )
}

// Identity is never colour alone: every series is named in the legend and the
// tooltip, with its total (of the days measured) beside it.
function Legend({ series }: { series: Series[] }): JSX.Element | null {
  if (series.length < 2) return null
  return (
    <div className="flex flex-wrap items-center gap-4 mt-2">
      {series.map((s) => (
        <div key={s.key} className="flex items-center gap-1.5 text-[11px] text-slate-400">
          <span className="inline-block rounded-sm" style={{ width: 10, height: 10, background: s.color }} />
          {s.label}
          <span className="text-slate-200 font-semibold">{fmt(s.values.reduce<number>((a, b) => a + (b ?? 0), 0))}</span>
        </div>
      ))}
    </div>
  )
}

function Tile({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }): JSX.Element {
  return (
    <div className="glass-card rounded-xl p-4 border border-white/5">
      <div className="flex items-center gap-1.5">
        {tone && <span className="inline-block rounded-sm" style={{ width: 8, height: 8, background: tone }} />}
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">{label}</div>
      </div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  )
}

function Panel({
  title,
  note,
  dates,
  series,
  selected,
  onSelect
}: {
  title: string
  note: string
  dates: string[]
  series: Series[]
  selected: string | null
  onSelect: (date: string | null) => void
}): JSX.Element {
  return (
    <div className="glass-card rounded-xl p-5">
      <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
      <div className="text-[11px] text-slate-500 mt-0.5 max-w-xl">{note}</div>
      <div className="mt-3">
        <TimeSeries dates={dates} series={series} selected={selected} onSelect={onSelect} />
        <Legend series={series} />
      </div>
    </div>
  )
}

// ── The table under the charts: one row per day, the numbers the charts draw ─

type Fetched = 'Full day' | 'Part of the day' | 'Not fetched' | 'Not reported'

interface DayRow {
  date: string
  fetched: Fetched
  coveredUntil: string | null
  /** null: the day was never fetched, so there is no figure. */
  mac: number | null
  win: number | null
  total: number | null
  ours: number | null
  signups: number | null
  active: number | null
  shares: number | null
  sampleInterval: number | null
  fetchedAt: string | null
}

const FETCHED: Record<DailyDownloads['coverage'], Fetched> = { full: 'Full day', partial: 'Part of the day', none: 'Not fetched' }

const DAY_COLUMNS: Column<DayRow>[] = [
  { key: 'date', label: 'Date', value: (d) => d.date, filter: 'text' },
  { key: 'fetched', label: 'From Cloudflare', value: (d) => d.fetched },
  { key: 'mac', label: 'macOS', value: (d) => d.mac, numeric: true },
  { key: 'win', label: 'Windows', value: (d) => d.win, numeric: true },
  { key: 'total', label: 'Downloads', value: (d) => d.total, numeric: true },
  { key: 'ours', label: 'Ours, left out', value: (d) => d.ours, numeric: true },
  { key: 'signups', label: 'Signups', value: (d) => d.signups, numeric: true },
  { key: 'active', label: 'Active accounts', value: (d) => d.active, numeric: true }
]

// A cell starting with = + - @ is a formula to a spreadsheet; none of these
// are typed by users, but the rule costs nothing.
const csvCell = (v: string): string => `"${(/^[=+\-@]/.test(v) ? `'${v}` : v).replace(/"/g, '""')}"`

function toCsv(rows: DayRow[]): string {
  const head = ['Date (UTC)', 'From Cloudflare', 'macOS downloads', 'Windows downloads', 'Downloads', 'Ours, left out', 'Signups', 'Active accounts', 'Share links', 'Cloudflare sample interval', 'Fetched at (UTC)']
  const val = (n: number | null): string => (n === null ? '' : String(n))
  const lines = rows.map((r) =>
    [r.date, r.fetched, val(r.mac), val(r.win), val(r.total), val(r.ours), val(r.signups), val(r.active), val(r.shares), val(r.sampleInterval), r.fetchedAt ?? ''].map(csvCell).join(',')
  )
  return [head.map(csvCell).join(','), ...lines].join('\r\n')
}

// One day, opened from the table or from a chart: everything stored for it.
// The request records behind a day's installs (server: download_requests).
function requests(date: string, os?: 'mac' | 'win', self = false): ExploreRef {
  return { set: 'downloadRequests', filters: { date, ...(os ? { os } : {}), self: self ? '1' : '0' } }
}

/** A table figure that opens its records; an unknown one stays a plain dash. */
function Rec({ v, to, label, k, testId }: { v: number | null; to: ExploreRef; label: string; k: string; testId?: string }): JSX.Element {
  if (v === null) return <>{fmtOr(v)}</>
  return (
    <DrillValue to={to} label={label} expect={{ key: k, value: v }} testId={testId}>
      {fmt(v)}
    </DrillValue>
  )
}

function DownloadDayDetail({ day, windowInstalls, daily }: { day: DayRow; windowInstalls: number; daily: DailyDownloads | undefined }): JSX.Element {
  const weekday = new Date(`${day.date}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
  const known = day.total !== null
  const fetchedText =
    day.fetched === 'Full day'
      ? 'All of the day'
      : day.fetched === 'Part of the day'
        ? `Part of the day${day.coveredUntil ? `, up to ${utc(day.coveredUntil)}` : ''}. The rest of it is not counted yet.`
        : day.fetched === 'Not fetched'
          ? 'Never fetched from Cloudflare, so there are no download figures for it. That is not the same as none.'
          : 'This server does not say which days were fetched; a 0 here may be a day never fetched.'
  const facts: Fact[] = [
    ['Day', `${weekday} ${day.date} (UTC)`],
    ['Fetched from Cloudflare', fetchedText],
    ['Downloads', known ? `${fmt(day.total as number)} installers served, our own addresses left out` : null],
    ['macOS', fmtOr(day.mac)],
    ['Windows', day.win === null ? null : `${fmt(day.win)} (an upper bound: the same .exe also applies updates)`],
    ['Ours, left out', day.ours === null ? null : `${fmt(day.ours)}${daily ? ` (${fmt(daily.mac?.installsSelf ?? 0)} macOS · ${fmt(daily.win?.installsSelf ?? 0)} Windows)` : ''}`],
    ['Share of the window', !known ? null : windowInstalls > 0 ? `${(((day.total as number) / windowInstalls) * 100).toFixed(1)}% of ${fmt(windowInstalls)}` : 'No downloads in the window'],
    ['Signups', fmtOr(day.signups)],
    ['Signups per download', !known || day.signups === null ? null : (day.total as number) > 0 ? `${Math.round((day.signups / (day.total as number)) * 100)}%` : 'No downloads that day to compare'],
    ['Active accounts (floor)', fmtOr(day.active)],
    ['Share links created', fmtOr(day.shares)],
    [
      'Sampling',
      !known
        ? null
        : day.sampleInterval && day.sampleInterval > 1
          ? `Cloudflare kept 1 request in ${day.sampleInterval}, so ${fmt(day.total as number)} is about ${fmt(Math.round((day.total as number) / day.sampleInterval))} observed requests, ±${day.sampleInterval}`
          : day.sampleInterval === 1
            ? 'Not sampled: every request counted'
            : 'No requests that day, so no sampling to report'
    ],
    ['Last pulled for this day', day.fetchedAt ? utc(day.fetchedAt) : null]
  ]
  return (
    <DetailGrid facts={facts}>
      <span className="text-xs text-slate-500 self-center">Records:</span>
      <DrillValue to={requests(day.date)} label={`Downloads on ${day.date}`} expect={known ? { key: 'requests', value: day.total as number } : undefined} testId="downloads-day-requests">
        <span className="btn-ghost text-xs inline-block">Installer requests</span>
      </DrillValue>
      <DrillValue to={requests(day.date, undefined, true)} label={`Our own downloads on ${day.date}, left out`} expect={day.ours === null ? undefined : { key: 'requests', value: day.ours }}>
        <span className="btn-ghost text-xs inline-block">Ours, left out</span>
      </DrillValue>
      <DrillValue to={{ set: 'accounts', filters: { createdOn: day.date } }} label={`Signups on ${day.date}`} expect={day.signups === null ? undefined : { key: 'count', value: day.signups }}>
        <span className="btn-ghost text-xs inline-block">Signups</span>
      </DrillValue>
      <DrillValue to={{ set: 'activeDays', filters: { day: day.date } }} label={`Active accounts on ${day.date}`} expect={day.active === null ? undefined : { key: 'count', value: day.active }}>
        <span className="btn-ghost text-xs inline-block">Active accounts</span>
      </DrillValue>
      <DrillValue to={{ set: 'shares', filters: { on: day.date } }} label={`Share links created on ${day.date}`} expect={day.shares === null ? undefined : { key: 'count', value: day.shares }}>
        <span className="btn-ghost text-xs inline-block">Share links</span>
      </DrillValue>
    </DetailGrid>
  )
}

export default function Downloads(): JSX.Element {
  const session = useAuth()
  const [days, setDays] = useState<number>(30)
  const [data, setData] = useState<DownloadSummary | null>(null)
  const [metrics, setMetrics] = useState<RemoteDailyMetric[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  // The open day: chosen on a chart or in the table, one at a time.
  const [selected, setSelected] = useState<string | null>(null)
  const fromChart = useRef(false)

  const load = useCallback(async (): Promise<void> => {
    const res = await getDownloads(days)
    if (res.ok) {
      setData(res.data.downloads)
      setMetrics(res.data.metrics)
      setState('ready')
    } else {
      setError(res.error)
      setState('error')
    }
  }, [days])

  useEffect(() => {
    void load()
  }, [load])

  async function onRefresh(): Promise<void> {
    setRefreshing(true)
    setNotice(null)
    const res = await refreshDownloads(days)
    setRefreshing(false)
    if (res.ok) {
      setData(res.data.downloads)
      setNotice(`Pulled ${res.data.rowsWritten} day-rows from Cloudflare.`)
      void load()
    } else {
      setNotice(
        res.status === 403
          ? 'Refreshing needs an admin or owner account. Nothing was changed.'
          : `Could not refresh: ${res.error}`
      )
    }
  }

  const dates = useMemo(() => (data?.installs ?? []).map((d) => d.date), [data])
  const dailyByDate = useMemo(() => new Map((data?.daily ?? []).map((d) => [d.date, d])), [data])

  const installSeries: Series[] = useMemo(
    () => [
      { key: 'mac', label: 'macOS', color: MAC, values: (data?.installs ?? []).map((d) => d.mac) },
      { key: 'win', label: 'Windows', color: WIN, values: (data?.installs ?? []).map((d) => d.win) }
    ],
    [data]
  )
  // The metrics window is built the same way (one row per day, oldest first),
  // but it is a different query, so it is aligned by date rather than index. A
  // date it does not carry is unknown, not zero.
  const metricsByDate = useMemo(() => new Map(metrics.map((m) => [m.date, m])), [metrics])
  const signupSeries: Series[] = useMemo(
    () => [{ key: 'signups', label: 'Signups', color: SIGNUP, values: dates.map((d) => metricsByDate.get(d)?.signups ?? null) }],
    [metricsByDate, dates]
  )

  const dayRows: DayRow[] = useMemo(
    () =>
      (data?.installs ?? []).map((d) => {
        const daily = dailyByDate.get(d.date)
        const m = metricsByDate.get(d.date)
        const total = d.mac === null && d.win === null ? null : (d.mac ?? 0) + (d.win ?? 0)
        const intervals = [daily?.mac?.sampleInterval, daily?.win?.sampleInterval].filter((v): v is number => typeof v === 'number')
        const fetchedTimes = [daily?.mac?.fetchedAt, daily?.win?.fetchedAt].filter((v): v is string => !!v).sort()
        return {
          date: d.date,
          fetched: daily ? FETCHED[daily.coverage] : 'Not reported',
          coveredUntil: daily?.coveredUntil ?? null,
          mac: d.mac,
          win: d.win,
          total,
          ours: daily && daily.coverage !== 'none' ? (daily.mac?.installsSelf ?? 0) + (daily.win?.installsSelf ?? 0) : null,
          signups: m?.signups ?? null,
          active: m?.dau ?? null,
          shares: m?.shareLinksCreated ?? null,
          // An older server sends no per-day figures: fall back to the window's rate.
          sampleInterval: intervals.length ? Math.max(...intervals) : daily ? null : data?.sampleInterval || null,
          fetchedAt: fetchedTimes.at(-1) ?? null
        }
      }),
    [data, dailyByDate, metricsByDate]
  )
  const tbl = useTableControls(dayRows, DAY_COLUMNS, 'downloads')

  // A day picked on a chart: make sure its row is shown, then bring it into view.
  const selectFromChart = (date: string | null): void => {
    fromChart.current = true
    if (date && tbl.activeCount > 0 && !tbl.rows.some((r) => r.date === date)) tbl.clearAll()
    setSelected(date)
  }
  useEffect(() => {
    if (!selected || !fromChart.current) return
    fromChart.current = false
    document.querySelector(`[data-testid="downloads-day-${selected}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selected, tbl.rows])

  const header = (
    <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Growth</div>
        <h1 className="text-2xl font-semibold mt-1">Downloads</h1>
        <div className="text-sm text-slate-400 mt-1 max-w-2xl">
          Installers actually served by Cloudflare on {data?.config.host ?? 'dl.plexiidesk.com'} — not
          clicks. GA counts clicks on pages we tag, which misses the download-link email, old links
          and anyone who declined the banner.
        </div>
      </div>
      <div className="flex items-center gap-2">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setDays(r)}
            data-testid={`range-${r}`}
            className={`px-3 py-1.5 rounded-md text-sm border ${
              days === r ? 'border-accent/60 bg-accent/15 text-white' : 'border-white/10 text-slate-300 hover:border-white/20'
            }`}
          >
            {r}d
          </button>
        ))}
        <button onClick={() => void onRefresh()} disabled={refreshing} data-testid="refresh" className="btn-primary disabled:opacity-40">
          {refreshing ? 'Pulling…' : 'Refresh from Cloudflare'}
        </button>
      </div>
    </div>
  )

  if (state !== 'ready' || !data) {
    return (
      <div>
        {header}
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {state === 'loading' ? 'Loading downloads…' : `Could not load downloads: ${error}`}
        </div>
      </div>
    )
  }

  const t = data.totals
  const installTotal = t.installs.mac + t.installs.win
  const selfTotal = t.installsSelf.mac + t.installsSelf.win
  const signupTotal = signupSeries[0].values.reduce<number>((a, b) => a + (b ?? 0), 0)
  const hasAnything = installTotal + selfTotal > 0
  const cov = data.coverage
  const fetchedDays = cov ? cov.full + cov.partial : null

  // What the table's shown rows add up to, from the days that have figures.
  const shownKnown = tbl.rows.filter((r) => r.total !== null)
  const sum = (k: 'mac' | 'win' | 'total' | 'ours' | 'signups' | 'active'): number => tbl.rows.reduce((a, r) => a + (r[k] ?? 0), 0)
  const whole = tbl.rows.length === dayRows.length && dates.length > 0

  function downloadCsv(): void {
    const blob = new Blob([toCsv(tbl.rows)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `plexii-downloads-${days}d-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <div>
      {header}

      {notice && (
        <div className="mb-4 glass-card rounded-lg px-4 py-2 text-sm text-slate-200" role="status">
          {notice}
        </div>
      )}

      {!data.config.configured && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-amber-500/10 text-amber-200" data-testid="not-configured">
          <div className="font-medium">{data.config.reason}</div>
          <div className="text-amber-200/70 text-[12px] mt-1">
            Set <code>CLOUDFLARE_ANALYTICS_TOKEN</code> (a token with Analytics&nbsp;Read on the
            plexiidesk.com zone) and <code>CLOUDFLARE_ZONE_ID</code> on the server, plus{' '}
            <code>DOWNLOAD_SELF_IPS</code> with your own address so your machines are not counted as
            demand. Anything already stored is still shown below.
          </div>
        </div>
      )}

      {cov && cov.none > 0 && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-white/[0.04] text-slate-200 border border-white/10" data-testid="coverage-gap">
          {cov.none === data.days
            ? `None of these ${data.days} days has been fetched from Cloudflare, so there are no download figures for them. That is not the same as no downloads.`
            : `${fmt(cov.none)} of these ${data.days} days were never fetched from Cloudflare. They are shaded gaps in the charts and “Not fetched” in the table, not zeros, and the totals leave them out.`}{' '}
          <span className="text-slate-400">“Refresh from Cloudflare” asks for the last {days} days, as far back as Cloudflare still keeps them.</span>
        </div>
      )}
      {!cov && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-amber-500/10 text-amber-200" data-testid="coverage-unknown">
          This server does not say which days were fetched from Cloudflare, so a 0 below may be a day nobody fetched.
        </div>
      )}

      {!hasAnything && (
        <div className="mb-4 glass-card rounded-xl p-6 text-sm text-slate-400" data-testid="no-data">
          <div>No download data stored yet.</div>
          <div className="text-slate-500 mt-1">
            Press “Refresh from Cloudflare” to pull the window. Nothing is counted until then — this
            page does not estimate.
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-5">
        <Drillable to={{ set: 'downloadDays', filters: { from: dates[0] ?? '' } }} label={`Downloads, last ${days} days`} expect={{ key: 'installs', value: installTotal }} testId="downloads-tile-installs">
          <Tile
            label={`Downloads · ${days}d`}
            value={fmt(installTotal)}
            hint={`${fmt(t.installs.mac)} macOS · ${fmt(t.installs.win)} Windows${fetchedDays !== null && fetchedDays < data.days ? ` · ${fetchedDays} of ${data.days} days fetched` : ''}`}
          />
        </Drillable>
        <Drillable to={{ set: 'downloadDays', filters: { from: dates[0] ?? '' } }} label={`Our own downloads, last ${days} days`} expect={{ key: 'installsSelf', value: selfTotal }}>
          <Tile
            label="Excluded as ours"
            value={fmt(selfTotal)}
            hint={data.config.selfIps.length ? `from ${data.config.selfIps.join(', ')}` : 'no self-IP filter set — this is 0 because nothing is being excluded'}
          />
        </Drillable>
        <Drillable to={{ set: 'accounts', filters: { from: dates[0] ?? '', to: dates[dates.length - 1] ?? '' } }} label={`Signups, last ${days} days`} expect={{ key: 'count', value: signupTotal }}>
          <Tile
            label={`Signups · ${days}d`}
            value={fmt(signupTotal)}
            tone={SIGNUP}
            hint={installTotal > 0 ? `${Math.round((signupTotal / installTotal) * 100)}% of downloads` : 'no downloads in range to compare'}
          />
        </Drillable>
      </div>

      <div className="space-y-4">
        <Panel
          title="Downloads per day"
          note="A person fetching an installer, with our own addresses removed. Windows is an upper bound: the same .exe also applies updates. Click a day to open it below."
          dates={dates}
          series={installSeries}
          selected={selected}
          onSelect={selectFromChart}
        />
        <Panel
          title="Signups per day"
          note="New accounts, counted from the account records themselves (an account deleted since is no longer counted). The gap between this and downloads is how many people installed but never signed in."
          dates={dates}
          series={signupSeries}
          selected={selected}
          onSelect={selectFromChart}
        />
      </div>

      <section className="mt-6" data-testid="table-view">
        <div className="mb-2">
          <h2 className="text-sm font-semibold text-slate-100">The figures behind the charts</h2>
          <div className="text-[11px] text-slate-500 mt-0.5">
            One row per day, exactly as the server stored it. Click a row, or a day on a chart, for everything about that day.
          </div>
        </div>
        <TableToolbar t={tbl} placeholder="Search a date…" noun="days">
          <button className="btn-ghost text-xs" onClick={downloadCsv} disabled={tbl.rows.length === 0} data-testid="downloads-csv">
            Download CSV
          </button>
        </TableToolbar>
        <div className="glass-card rounded-xl overflow-x-auto">
          <table className="admin-table">
            <TableHead t={tbl} />
            <tbody>
              {tbl.rows.map((r) => (
                <ExpandableRow
                  key={r.date}
                  open={selected === r.date}
                  onToggle={() => setSelected(selected === r.date ? null : r.date)}
                  colSpan={DAY_COLUMNS.length}
                  testId={`downloads-day-${r.date}`}
                  detail={() => <DownloadDayDetail day={r} windowInstalls={installTotal} daily={dailyByDate.get(r.date)} />}
                >
                  <td className="text-slate-400 text-xs whitespace-nowrap">{r.date}</td>
                  <td className={`text-xs whitespace-nowrap ${r.fetched === 'Not fetched' ? 'text-amber-200' : r.fetched === 'Part of the day' ? 'text-slate-300' : 'text-slate-400'}`}>{r.fetched}</td>
                  <td className="tabular-nums"><Rec v={r.mac} to={requests(r.date, 'mac')} label={`macOS downloads on ${r.date}`} k="requests" /></td>
                  <td className="tabular-nums"><Rec v={r.win} to={requests(r.date, 'win')} label={`Windows downloads on ${r.date}`} k="requests" /></td>
                  <td className="tabular-nums"><Rec v={r.total} to={requests(r.date)} label={`Downloads on ${r.date}`} k="requests" testId={`downloads-drill-${r.date}`} /></td>
                  <td className="tabular-nums text-slate-400"><Rec v={r.ours} to={requests(r.date, undefined, true)} label={`Our own downloads on ${r.date}, left out`} k="requests" /></td>
                  <td className="tabular-nums"><Rec v={r.signups} to={{ set: 'accounts', filters: { createdOn: r.date } }} label={`Signups on ${r.date}`} k="count" /></td>
                  <td className="tabular-nums text-slate-400"><Rec v={r.active} to={{ set: 'activeDays', filters: { day: r.date } }} label={`Active accounts on ${r.date}`} k="count" /></td>
                </ExpandableRow>
              ))}
            </tbody>
            {tbl.rows.length > 0 && (
              <tfoot>
                <tr data-testid="downloads-totals">
                  <td className="text-xs text-slate-300">Total</td>
                  <td className="text-xs text-slate-400">{fmt(shownKnown.length)} of {fmt(tbl.rows.length)} days with figures</td>
                  {/* Totals open their records only for the whole window: a
                      filtered table's days need not be one range. */}
                  <td className="tabular-nums font-semibold">{whole ? <Rec v={sum('mac')} to={{ set: 'downloadDays', filters: { from: dates[0], os: 'mac' } }} label={`macOS downloads, last ${days} days`} k="installs" /> : fmt(sum('mac'))}</td>
                  <td className="tabular-nums font-semibold">{whole ? <Rec v={sum('win')} to={{ set: 'downloadDays', filters: { from: dates[0], os: 'win' } }} label={`Windows downloads, last ${days} days`} k="installs" /> : fmt(sum('win'))}</td>
                  <td className="tabular-nums font-semibold">{whole ? <Rec v={sum('total')} to={{ set: 'downloadDays', filters: { from: dates[0] } }} label={`Downloads, last ${days} days`} k="installs" /> : fmt(sum('total'))}</td>
                  <td className="tabular-nums text-slate-400">{whole ? <Rec v={sum('ours')} to={{ set: 'downloadDays', filters: { from: dates[0] } }} label={`Our own downloads, last ${days} days`} k="installsSelf" /> : fmt(sum('ours'))}</td>
                  <td className="tabular-nums font-semibold">{whole ? <Rec v={sum('signups')} to={{ set: 'accounts', filters: { from: dates[0], to: dates[dates.length - 1] } }} label={`Signups, last ${days} days`} k="count" /> : fmt(sum('signups'))}</td>
                  <td className="text-xs text-slate-500">not summed: one person counts on many days</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      <div className="mt-5 text-[11px] text-slate-500 space-y-1 max-w-3xl">
        {data.sampleInterval > 1 && (
          <div data-testid="sampling-note">
            Cloudflare sampled 1 request in {data.sampleInterval} and scaled the counts back up, so
            every download figure is granular to about ±{data.sampleInterval} — a day showing{' '}
            {data.sampleInterval} is one observed request. Read the shape, not a single day.
          </div>
        )}
        <div>
          Only new downloads are counted here: installed copies checking for, or fetching, an update
          are left out.
        </div>
        {data.lastFetchedAt && (
          <div>
            Last pulled from Cloudflare {utc(data.lastFetchedAt)}
            {session?.role === 'support' ? ' · refreshing needs an admin account' : ''}
          </div>
        )}
      </div>
    </div>
  )
}
